import uniqueId from 'lodash/uniqueId';

import { UseSelectedCutoutLaborResponse } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import {
  AreaCalculationType,
  BACKSPLASH_ID,
  LIST_ITEM,
  UNIT_MEASURE,
  WATERFALL_ID,
} from '~/core/constants';
import { isRadiusOut } from '~/core/enums';
import type {
  ApplianceModel,
  ColorModel,
  CommonProductModel,
  CornerRadiusModel,
  CutoutLaborModel,
  DiagonalCornerModel,
  DrawingAppliance,
  DrawingEdge,
  DrawingShape,
  PricedProduct,
  ProductEstimateQuery,
  ProductQty,
  WaterfallLabor,
} from '~/core/models';
import { ApplianceType, getCornerRadiusFromSize } from '~/core/models';
import { ApplianceRecord } from '~/core/types/appliances';
import {
  hasRequiredProps,
  incById,
  isActualProduct,
  isCustomerSuppliedAppliance,
} from '~/core/utils';
import { isShapeOversizeIsland } from '~/core/utils/area.utils';
import type { CookingAppliancesState } from '~/redux/slices/cookingAppliance';
import type { CutoutState } from '~/redux/slices/cutout';
import type { TearOutState } from '~/redux/slices/tearOut';
import type { FaucetContextValue } from '~contexts';

// ============================================================================
// PRODUCT ESTIMATE QUERY CONTEXT TYPES
// ============================================================================

/**
 * Context for processing drawing shapes into product quantities.
 */
export interface DrawingShapeProcessingContext {
  insideDiagonalCorner: DiagonalCornerModel | undefined;
  cornerRadii: CornerRadiusModel[];
  oversizeFeeProduct: CommonProductModel | undefined;
  selectedMaterialAreaType: AreaCalculationType;
}

/**
 * Configuration for adding side item products.
 */
export interface SideItemProductsConfig {
  totalBacksplashArea: number;
  totalWaterfallArea: number;
  totalWaterfallLaborEdgeLengths: number;
  waterfallLaborFee: WaterfallLabor | undefined;
  totalEndCapLaborEdgeCount: number;
  matchingEndCap: CommonProductModel | undefined;
  totalEndSplashLaborEdgeCount: number;
  matchingEndSplash: CommonProductModel | undefined;
  isSmallJobWithFee: boolean;
  smallJobFeeProduct: CommonProductModel | undefined;
  selectedColorProductId: number;
}

/**
 * Context for mapping completed products.
 */
export interface ProductMappingContext {
  selectedColor: ColorModel;
  backsplashTitle: string;
  waterfallTitle: string;
}

/**
 * This maps a selectedSinks object to an array of {productId, quantity} (i.e. pricedProducts) records
 * @param selectedSinks
 */
export const mapSinkRecordToPricedSinks = (
  selectedSinks: Record<ApplianceModel['productId'], number>
) =>
  Object.entries(selectedSinks).map<ProductQty>(([id, quantity]) => ({
    productId: +id,
    quantity,
  }));

export const getPricedSinksCutouts = (
  pricedSinks: ProductQty[],
  sinksProducts: ApplianceModel[],
  selectSinkCutout: UseSelectedCutoutLaborResponse['selectSinkCutout'],
  productLine: string | null | undefined
) =>
  pricedSinks.reduce<ProductQty[]>((acc, pricedSink) => {
    const sinkAppliance = sinksProducts.find(
      (sink) => sink.productId === pricedSink.productId
    );

    if (sinkAppliance?.applianceType === ApplianceType.SINK) {
      const cutoutLaborProduct = selectSinkCutout({
        materialType: productLine,
        sink: sinkAppliance,
        _source: 'pricedProducts',
      });

      if (cutoutLaborProduct) {
        return [
          ...acc,
          {
            productId: cutoutLaborProduct.productId,
            quantity: pricedSink.quantity,
          },
        ];
      }
    }

    return acc;
  }, []);

export const getPricedTearOuts = (
  selectedTearOuts: TearOutState['selectedTearOuts']
) =>
  Object.entries(selectedTearOuts)
    .filter(([, qty]) => qty > 0)
    .map<ProductQty>(([productId, qty]) => ({
      productId: Number(productId),
      quantity: qty,
    }));

export const getPricedCutouts = (
  selectedCutouts: CutoutState['customCutouts']['selected']
) =>
  Object.entries(selectedCutouts)
    .filter(([, qty]) => (qty.onCard ?? 0) > 0 || (qty.onShape ?? 0) > 0)
    .map<ProductQty>(([productId, qty]) => {
      const totalQuantity = (qty.onCard ?? 0) + (qty.onShape ?? 0);

      return {
        productId: Number(productId),
        quantity: totalQuantity,
      };
    })
    .filter((item) => item.quantity > 0);

export const getPricedApplianceCutouts = (
  applianceCutouts: CutoutState['applianceCutouts']['selected']
) =>
  Object.entries(applianceCutouts)
    .flatMap(([cutoutId, applianceRecord]) =>
      Object.entries(applianceRecord).map(([_applianceId, quantity]) => ({
        productId: Number(cutoutId),
        quantity,
      }))
    )
    .filter((item) => item.quantity > 0);

export const getPricedFaucets = (
  selectedFaucets: FaucetContextValue['selectedFaucets']
) =>
  Object.entries(selectedFaucets)
    .filter(([, faucet]) => faucet.isSelected && faucet.quantity > 0)
    .map<ProductQty>(([productId, faucet]) => ({
      productId: Number(productId),
      quantity: faucet.quantity,
    }));

/**
 * Maps all _actual_ cooking appliances from the selectedCookingAppliances to pricedProducts with quantities
 * Customer supplied appliances are removed
 * @param selectedCooktops
 * @param selectedRanges
 * @param allCookingAppliances
 */
export const getPricedCookingAppliances = (
  selectedCooktops: CookingAppliancesState['selectedCooktopApplianceProduct']['selected'],
  selectedRanges: CookingAppliancesState['selectedRangeApplianceProduct']['selected'],
  allCookingAppliances: ApplianceModel[] = []
) => {
  const applianceEntries = [selectedCooktops, selectedRanges].flatMap(
    (cooktopAppliance) => Object.entries(cooktopAppliance)
  );

  const appliancesWithQuantity = applianceEntries.filter(
    ([, qty]) => (qty.onCard ?? 0) > 0 || (qty.onShape ?? 0) > 0
  );

  const productQuantities = appliancesWithQuantity.map<ProductQty>(
    ([productId, qty]) => ({
      productId: Number(productId),
      quantity: (qty.onCard ?? 0) + (qty.onShape ?? 0),
    })
  );

  return productQuantities.filter(
    ({ productId }) =>
      !isCustomerSuppliedAppliance(
        allCookingAppliances.find(
          (appliance) => appliance.productId === productId
        )
      )
  );
};

/**
 * Calculate the total quantity of cooktopCutouts based on the selectedCooktops
 * @param selectedCooktops all of the cooktops quantities, including in-shape, just added, real, or customer supplied
 * @param cooktopCutout the cutout to use for each cooktop being added
 */
export const getPricedCooktopCutouts = (
  selectedCooktops: ApplianceRecord,
  cooktopCutout: CutoutLaborModel | undefined
) => {
  const totalCooktopCutoutsNeeded = Object.values(selectedCooktops)
    .flatMap((something) => Object.values(something))
    .reduce((acc, val) => acc + val, 0);

  if (totalCooktopCutoutsNeeded && cooktopCutout) {
    return [
      {
        productId: cooktopCutout?.productId,
        quantity: totalCooktopCutoutsNeeded,
      },
    ];
  }

  return [];
};

export const getCornerProducts = (
  drawingShape: DrawingShape,
  insideDiagonalCorner?: DiagonalCornerModel
) =>
  Object.values(drawingShape.cornerMap).reduce<Record<number, number>>(
    (corners, corner) => {
      const id =
        corner.hasPredefinedExtraEdging &&
        Boolean(corner.diagonalLength) &&
        (corner.angle ?? 0) < 0
          ? insideDiagonalCorner?.productId
          : corner.productId;

      return incById(corners, corner.isVisibleInCanvas && id);
    },
    {}
  );

export const getEdgeProducts = (
  drawingShape: DrawingShape,
  cornerRadii: CornerRadiusModel[]
): Record<number, number> =>
  Object.values(drawingShape.drawingEdges)
    .filter(
      (edge) =>
        isEdgeWithValidRadius(edge) && hasRequiredProps(edge, ['realRadius'])
    )
    .map((edge) =>
      edge.realRadius
        ? getCornerRadiusFromSize(edge.realRadius, cornerRadii)
        : undefined
    )
    .filter(
      (cornerRadius): cornerRadius is CornerRadiusModel =>
        cornerRadius !== undefined && isActualProduct(cornerRadius.productId)
    )
    .map((cornerRadius) => cornerRadius.productId)
    .reduce<Record<number, number>>(incById, {});

const isEdgeWithValidRadius = (edge: DrawingEdge): boolean =>
  isRadiusOut(edge) && edge.isVisibleInCanvas && edge.realRadius !== undefined;

/**
 * Add any other appliances that haven't already been accounted for
 * @param productEstimateQuery the current productEstimateQuery, which will be mutated
 */
export const addOtherAppliances =
  (productEstimateQuery: ProductEstimateQuery) =>
  (appliance: DrawingAppliance) => {
    const { applianceType, productId } = appliance.applianceModel;

    if (
      applianceType === ApplianceType.DISHWASHER ||
      applianceType === ApplianceType.REFRIGERATOR
    ) {
      productEstimateQuery.products.push({ productId, quantity: 1 });
    }
  };

export const accumulateDuplicateCornerRadii = (
  estimateQueryProducts: ProductQty[]
) => {
  const products = [...estimateQueryProducts];

  return products
    .reduce((accumulator: ProductQty[], currentProductQty: ProductQty) => {
      const acc = [...accumulator];
      const duplicateProd = acc.findIndex(
        ({ productId }) =>
          !currentProductQty.lineItemId &&
          productId === currentProductQty.productId
      );

      if (duplicateProd !== -1) {
        acc[duplicateProd] = {
          ...acc[duplicateProd],
          quantity: acc[duplicateProd].quantity + currentProductQty.quantity,
        };
      } else {
        acc.push(currentProductQty);
      }

      return acc;
    }, [])
    .filter((product) => product.quantity > 0);
};

export const getMappedPricedProduct = (
  product: PricedProduct,
  selectedColor: ColorModel,
  backsplashTitle: string,
  waterfallTitle: string
) => {
  // rename backsplashes and waterfalls
  // so they're not the same as the main countertop
  const name = {
    [BACKSPLASH_ID]: backsplashTitle,
    [WATERFALL_ID]: waterfallTitle,
  }[product.lineItemId ?? -1];
  const nameDesc = name
    ? { name, consumerDesc: name, abbrevDesc: name }
    : undefined;
  // This overrides the unit of measure for any of the SLAB products
  // because they're "ea" in the database and need to be "sq ft" here.
  const unitOfMeasure =
    product.productId === selectedColor?.productId
      ? UNIT_MEASURE.MATERIALS
      : product.unitOfMeasure;

  return {
    ...product,
    listKey: `${LIST_ITEM}_${uniqueId()}`,
    unitOfMeasure,
    ...nameDesc,
  };
};

export const getNumberOfChipMinimizersInDrawingShapes = (
  drawingShapes: DrawingShape[]
): number =>
  drawingShapes.reduce((acc, next) => {
    next.appliances.forEach((appliance) => {
      if (appliance.hasChipMinimizer) {
        acc += 1;
      }
    });

    return acc;
  }, 0);

// ============================================================================
// DRAWING SHAPE PRODUCT PROCESSING
// ============================================================================

/**
 * Gets products from a single drawing shape including corners, edges, and oversize fees.
 */
export const getProductsFromShape = (
  drawingShape: DrawingShape,
  context: DrawingShapeProcessingContext
): ProductQty[] => {
  const products: ProductQty[] = [];

  // Get corner and edge products
  const cornerProds = getCornerProducts(
    drawingShape,
    context.insideDiagonalCorner
  );
  const edgeProds = getEdgeProducts(drawingShape, context.cornerRadii);

  // Convert to ProductQty array
  [...Object.entries(cornerProds), ...Object.entries(edgeProds)].forEach(
    ([id, quantity]) => {
      products.push({ productId: Number(id), quantity });
    }
  );

  // Add oversize fee for islands if applicable
  if (
    context.oversizeFeeProduct &&
    isShapeOversizeIsland(drawingShape, context.selectedMaterialAreaType)
  ) {
    products.push({
      productId: context.oversizeFeeProduct.productId,
      quantity: 1,
    });
  }

  return products;
};

/**
 * Processes all drawing shapes and adds products to the estimate query.
 * Also adds other appliances (dishwashers, refrigerators).
 */
export const processDrawingShapes = (
  drawingShapes: DrawingShape[],
  context: DrawingShapeProcessingContext,
  productEstimateQuery: ProductEstimateQuery
): void => {
  drawingShapes.forEach((drawingShape: DrawingShape) => {
    const shapeProducts = getProductsFromShape(drawingShape, context);

    productEstimateQuery.products.push(...shapeProducts);

    // Add other in-drawing appliances
    drawingShape.appliances.forEach(addOtherAppliances(productEstimateQuery));
  });
};

// ============================================================================
// SIDE ITEM PRODUCT ADDITIONS
// ============================================================================

/**
 * Adds all side item products to the estimate query.
 * Includes backsplash, waterfall, labor fees, and small job fees.
 */
export const addSideItemProducts = (
  config: SideItemProductsConfig,
  productEstimateQuery: ProductEstimateQuery
): void => {
  const { products } = productEstimateQuery;

  // Backsplash
  if (config.totalBacksplashArea) {
    products.push({
      productId: config.selectedColorProductId,
      quantity: config.totalBacksplashArea,
      lineItemId: BACKSPLASH_ID,
    });
  }

  // Waterfall
  if (config.totalWaterfallArea) {
    products.push({
      productId: config.selectedColorProductId,
      quantity: config.totalWaterfallArea,
      lineItemId: WATERFALL_ID,
    });
  }

  // Waterfall labor
  if (config.totalWaterfallLaborEdgeLengths && config.waterfallLaborFee) {
    products.push({
      productId: config.waterfallLaborFee.productId,
      quantity: config.totalWaterfallLaborEdgeLengths,
      lineItemId: config.waterfallLaborFee.lineItemId,
    });
  }

  // EndCap labor
  if (config.totalEndCapLaborEdgeCount && config.matchingEndCap) {
    products.push({
      productId: config.matchingEndCap.productId,
      quantity: config.totalEndCapLaborEdgeCount,
    });
  }

  // EndSplash labor
  if (config.totalEndSplashLaborEdgeCount && config.matchingEndSplash) {
    products.push({
      productId: config.matchingEndSplash.productId,
      quantity: config.totalEndSplashLaborEdgeCount,
    });
  }

  // Small job fee
  if (config.isSmallJobWithFee && config.smallJobFeeProduct?.productId) {
    products.push({
      productId: config.smallJobFeeProduct.productId,
      quantity: 1,
    });
  }
};

// ============================================================================
// PRODUCT DEDUPLICATION
// ============================================================================

/**
 * Reduces duplicate products by combining quantities.
 * Products with lineItemId are NOT combined (they are unique line items).
 */
export const reduceDuplicateProducts = (products: ProductQty[]): ProductQty[] =>
  products
    .reduce<ProductQty[]>((accumulator, currentProductQty) => {
      const duplicateIndex = accumulator.findIndex(
        (productQty) =>
          productQty.productId === currentProductQty.productId &&
          !currentProductQty.lineItemId
      );

      if (duplicateIndex !== -1) {
        accumulator[duplicateIndex] = {
          ...accumulator[duplicateIndex],
          quantity:
            accumulator[duplicateIndex].quantity + currentProductQty.quantity,
        };
      } else {
        accumulator.push(currentProductQty);
      }

      return accumulator;
    }, [])
    .filter((product) => product.quantity > 0);

// ============================================================================
// PRICE FETCHING
// ============================================================================

/**
 * Fetches prices for unknown products from the API.
 * Returns completed priced products including both catalog and API results.
 */
export const fetchUnknownProductPrices = async (
  unknownProductsQuery: ProductEstimateQuery,
  completedPricedProducts: PricedProduct[],
  fetchLazyProductsPrices: (
    params: { data: ProductEstimateQuery },
    preferCache: boolean
  ) => { unwrap: () => Promise<PricedProduct[]> }
): Promise<PricedProduct[]> => {
  // Filter to only actual products
  const filteredProducts = unknownProductsQuery.products.filter((productQty) =>
    isActualProduct(productQty.productId)
  );

  // If no unknown products, return what we have
  if (!filteredProducts.length) {
    return completedPricedProducts;
  }

  // Update query with filtered products
  unknownProductsQuery.products = filteredProducts;

  // Fetch prices from API
  const data = await fetchLazyProductsPrices(
    { data: unknownProductsQuery },
    true
  ).unwrap();

  if (data.length) {
    const returnedPricedProducts: PricedProduct[] = data.map(
      (pricedProduct: PricedProduct) => ({
        ...pricedProduct,
        adjustedQty: pricedProduct.quantity,
      })
    );

    return [...completedPricedProducts, ...returnedPricedProducts];
  }

  return completedPricedProducts;
};

// ============================================================================
// PRODUCT MAPPING
// ============================================================================

/**
 * Maps completed priced products with translations and formatting.
 */
export const mapCompletedProducts = (
  completedPricedProducts: PricedProduct[],
  context: ProductMappingContext
): PricedProduct[] =>
  completedPricedProducts.map((product) =>
    getMappedPricedProduct(
      product,
      context.selectedColor,
      context.backsplashTitle,
      context.waterfallTitle
    )
  );
